import { ApolloQueryResult } from '@apollo/client';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import {
  AutomationRunStatus,
  DateRangeOutput,
  GetPaginatedAutomationRunsQuery,
  SortOrder,
  SubscribeToClinicUserChannelsDocument,
  useGetPaginatedAutomationRunsQuery,
} from 'shared/types/graphql';

interface UseGetPaginatedAutomationRunsProps {
  clinicId: string;
  numberOfAutomationRunsPerPage?: number;
  automationRunStatus: AutomationRunStatus;
  dateRange?: DateRangeOutput | null;
  shouldSkip?: boolean;
}

interface UseGetPaginatedAutomationRunsReturn {
  automationRunsPage: number;
  setAutomationRunsPage: Dispatch<SetStateAction<number>>;
  automationRuns?: GetPaginatedAutomationRunsQuery['automationRuns'];
  totalAutomationRuns?: number;
  refetchAutomationRuns: () => Promise<ApolloQueryResult<GetPaginatedAutomationRunsQuery>>;
  isLoadingAutomationRuns: boolean;
  hasNextPage: boolean;
}

export const DEFAULT_ITEMS_PER_PAGE = 10;

export function useGetPaginatedAutomationRuns({
  clinicId,
  numberOfAutomationRunsPerPage = DEFAULT_ITEMS_PER_PAGE,
  dateRange,
  automationRunStatus,
  shouldSkip,
}: UseGetPaginatedAutomationRunsProps): UseGetPaginatedAutomationRunsReturn {
  const [automationRunsPage, setAutomationRunsPage] = useState(1);

  const queryVarsWhere = useMemo(
    () => ({
      clinicId: { equals: clinicId },
      updatedAt: { gte: dateRange?.gte, lte: dateRange?.lte },
      status: { equals: automationRunStatus },
    }),
    [automationRunStatus, clinicId, dateRange?.gte, dateRange?.lte],
  );

  const {
    data: automationRunsData,
    refetch: refetchAutomationRuns,
    loading: isLoadingAutomationRuns,
    subscribeToMore: subscribeToChannels,
  } = useGetPaginatedAutomationRunsQuery({
    variables: {
      where: queryVarsWhere,
      take: numberOfAutomationRunsPerPage,
      skip: (automationRunsPage - 1) * numberOfAutomationRunsPerPage,
      orderBy: { updatedAt: SortOrder.Desc },
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    skip: !clinicId || shouldSkip,
  });

  const totalAutomationRuns = useMemo(() => automationRunsData?.count, [automationRunsData]);

  const hasNextPage = useMemo(() => {
    return totalAutomationRuns !== 0 && (totalAutomationRuns || 0) > automationRunsPage * DEFAULT_ITEMS_PER_PAGE;
  }, [automationRunsPage, totalAutomationRuns]);

  subscribeToChannels({
    document: SubscribeToClinicUserChannelsDocument,
    variables: { clinicId },
    updateQuery: (prev) => {
      if (prev.automationRuns?.length && clinicId) {
        if (automationRunStatus) {
          refetchAutomationRuns({
            where: {
              ...queryVarsWhere,
              status: { equals: automationRunStatus },
            },
          });
        }
        return prev;
      } else {
        return { channels: [], count: 0, automationRuns: [] };
      }
    },
  });

  useEffect(() => {
    setAutomationRunsPage(1);
  }, [clinicId]);

  return {
    automationRunsPage,
    setAutomationRunsPage,
    automationRuns: automationRunsData?.automationRuns,
    totalAutomationRuns,
    refetchAutomationRuns,
    isLoadingAutomationRuns,
    hasNextPage,
  };
}
